import { createContext, useCallback, useState } from 'react';
import { NAVIGATION_ACTION_OUTCOME_VISIBLE_MS } from 'components/ui/navigationActionFeedback';
import type { ReviewSaveFeedback } from 'pages/Intelligence/DailyNav/components/ReviewPriceCell';
import type { DailyNavTableRow } from 'pages/Intelligence/DailyNav/hooks/useDailyNavSheet';

export const REVIEW_SAVE_PULSE_MS = NAVIGATION_ACTION_OUTCOME_VISIBLE_MS;

// Keyed by company id; only rows mid-save or just saved appear.
export type ReviewSaveFeedbackByCompany = Record<number, ReviewSaveFeedback>;

// Not threaded through the sheet parser: any parser input change rebuilds the whole spreadsheet.
export const ReviewSaveFeedbackContext = createContext<ReviewSaveFeedbackByCompany>({});

export interface UseReviewSaveFeedbackResult {
  reviewSaveFeedback: ReviewSaveFeedbackByCompany;
  onReviewSaving: (companyId: number) => void;
  onReviewSaved: (row: DailyNavTableRow) => Promise<void>;
  onReviewSaveFailed: (companyId: number) => void;
}

const withoutCompany = (feedback: ReviewSaveFeedbackByCompany, companyId: number) => {
  const next = { ...feedback };
  delete next[companyId];
  return next;
};

const useReviewSaveFeedback = (
  refreshRecord: (recordId: number) => Promise<void>
): UseReviewSaveFeedbackResult => {
  const [reviewSaveFeedback, setReviewSaveFeedback] = useState<ReviewSaveFeedbackByCompany>({});

  const onReviewSaving = useCallback((companyId: number) => {
    setReviewSaveFeedback(current => ({ ...current, [companyId]: 'saving' }));
  }, []);

  const onReviewSaved = useCallback(
    async (row: DailyNavTableRow) => {
      try {
        await refreshRecord(row.record.id as number);
      } catch {
        // The save itself landed; a failed re-read only leaves the old status showing.
      }
      const { companyId } = row;
      setReviewSaveFeedback(current => ({ ...current, [companyId]: 'saved' }));
      setTimeout(() => {
        setReviewSaveFeedback(current => (current[companyId] === 'saved' ? withoutCompany(current, companyId) : current));
      }, REVIEW_SAVE_PULSE_MS);
    },
    [refreshRecord]
  );

  const onReviewSaveFailed = useCallback((companyId: number) => {
    setReviewSaveFeedback(current => withoutCompany(current, companyId));
  }, []);

  return { reviewSaveFeedback, onReviewSaving, onReviewSaved, onReviewSaveFailed };
};

export default useReviewSaveFeedback;
